import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef} from '@angular/core';
import {SignInOutput} from 'aws-amplify/auth';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';

import {CustomInputComponent} from '../../../components/custom-input/custom-input.component';
import {LABEL_CONSTANT, LOGIN_CONSTANT, PLACEHOLDER_CONSTANT} from '../../../constant/constant';
import {ValidationService} from '../../../shared/services/validation.service';
import {ButtonComponent} from '../../../components/button/button.component';
import {CognitoService} from '../../../core/auth/cognito.service';
import {CognitoOrgIdService} from '../../../core/auth/cognito-org-id.service';
import {SIGN_IN_STATE} from '../organization-login.component';
import {AppAuthorizationService} from '../../../core/auth/app-authorization.service';
import {HttpErrorService} from '../../../core/services/http-error.service';

export interface ISignInForm {
  userName: FormControl<string | null | undefined>;
  password: FormControl<string | null>;
  rememberUsername: FormControl<boolean | null>;
}

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [ReactiveFormsModule, CustomInputComponent, ButtonComponent],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent implements OnInit, OnDestroy {
  @Input() public signInState: string = '';
  @Input() public clientId?: string;
  @Input() public userName: string = '';
  @Output() setSignInState: EventEmitter<string> = new EventEmitter<string>();
  @Output() signInOuput: EventEmitter<any> = new EventEmitter<any>();
  @Output() setResetFlowUsername: EventEmitter<string> = new EventEmitter<string>();

  PLACEHOLDER_CONSTANT = PLACEHOLDER_CONSTANT;
  LABEL_CONSTANT = LABEL_CONSTANT;
  signInForm: FormGroup<ISignInForm>;
  isSignInLoading = false;

  constructor(
    private _cognitoService: CognitoService,
    private _cognitoOrgIdService: CognitoOrgIdService,
    private _appAuthorizationService: AppAuthorizationService,
    private _httpErrorService: HttpErrorService,
    private _validationService: ValidationService,
    private _cdr: ChangeDetectorRef
  ) {
    this.signInForm = new FormGroup<ISignInForm>({
      userName: new FormControl(null, [Validators.required, ValidationService.emailValidator()]),
      password: new FormControl(null, Validators.required),
      rememberUsername: new FormControl(false),
    });
  }

  ngOnInit(): void {
    const storedUsername = this.getUsernameFromStorage();
    const userName = this.userName || storedUsername;
    if (userName) {
      this.signInForm.patchValue({
        userName,
        rememberUsername: !!storedUsername
      });
    }
  }

  getUsernameFromStorage(){
    return localStorage.getItem('username') || '';
  }

  public switchToForgotPasswordFlow(): void {
    this.setSignInState.emit(SIGN_IN_STATE.FORGOT_PASSWORD);
  }

  public onSubmit(): void {
    if (this.signInForm.valid) {
      this.signIn();
    } else {
      // shows validation error if the username is not present
      this._validationService.validateAllFormFields(this.signInForm);
    }
  }

  private signIn(): void {
    this.isSignInLoading = true;
    const {['userName']: email, password, rememberUsername} = this.signInForm.getRawValue();
    this._cognitoService
      .signIn({
        email: email as string,
        password: password as string,
        rememberUsername: rememberUsername as boolean
      })
      .then((data: SignInOutput) => {
        if (
          data?.nextStep?.signInStep === LOGIN_CONSTANT.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
        ) {
          this.setSignInState.emit(SIGN_IN_STATE.CONFIRM_SIGN_IN);
          this.setResetFlowUsername.emit(email as string);
        } else if (data?.nextStep?.signInStep === LOGIN_CONSTANT.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) {
          this.setSignInState.emit(SIGN_IN_STATE.MFA_SETUP);
          this.setResetFlowUsername.emit(email as string);
          this.signInOuput.emit(data.nextStep);
        } else if (data?.nextStep?.signInStep === LOGIN_CONSTANT.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
          this.setSignInState.emit(SIGN_IN_STATE.MFA_PROMPT);
          this.signInOuput.emit(data.nextStep);
        } else {
          this._appAuthorizationService.routeToClientApp(
            this._cognitoOrgIdService.orgId() as string,
            this.clientId as string,
          );
        }
        this.isSignInLoading = false;
        this._cdr.detectChanges();
      })
      .catch((error: Error) => {
        console.error("Error signing in:", error.message);
        this.isSignInLoading = false;
        this._httpErrorService.httpError.set(error.message);
        this._cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this._httpErrorService.httpError.set('');
  }
}
